import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import 'Components/molecules/vm-content-popper/vm-content-popper';
import 'Components/molecules/vm-popover/vm-popover';
import 'Components/molecules/vm-dropdown/vm-dropdown';
import 'Components/molecules/vm-dropdown/vm-dropdown-item';
import * as Sentry from '@sentry/browser';
import { TanStackFormController } from '@tanstack/lit-form';
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { shareVideo } from 'Api/videos';
import { ApiFetchError } from 'Utils/api-fetch-request';
import Styles from './vm-video-share-menu.scss';

interface ShareFormValues {
    emails: string;
    message: string;
}

declare global {
    interface Window {
        gtag?: (command: string, action: string, params: Record<string, unknown>) => void;
    }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmails(input: string): { emails: string[]; invalid: string[] } {
    const emails: string[] = [];
    const invalid: string[] = [];
    input
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((candidate) => {
            if (EMAIL_RE.test(candidate)) {
                emails.push(candidate);
            } else {
                invalid.push(candidate);
            }
        });
    return { emails, invalid };
}

/**
 * Share menu for a video. Renders a "Share" trigger button that opens a
 * popover with three options:
 *
 * - Email video link — opens a content popper (modal on desktop, bottom
 *   sheet on mobile) with a TanStack Form for recipient emails + message,
 *   posting to `POST /api/videos/<slug>/share/`.
 * - Share on X — opens the X share intent in a new tab.
 * - Share on LinkedIn — opens the LinkedIn share intent in a new tab.
 *
 * Toasts: dispatches `vm-toast-show` events on `document` for success/error
 * notifications. Relies on a globally-mounted `<vm-toast-container>`.
 *
 * @example
 * ```html
 * <vm-video-share-menu
 *   video-slug="some-video-slug"
 *   video-url="https://www.vumedi.com/video/some-video-slug/"
 *   video-title="The Future of Acne Treatment"
 * ></vm-video-share-menu>
 * ```
 */
@customElement('vm-video-share-menu')
export class VmVideoShareMenu extends LitElement {
    static override styles = Styles;

    /** Slug of the video being shared. Used in the share API call. */
    @property({ type: String, attribute: 'video-slug' })
    videoSlug = '';

    /** Absolute URL of the video — used as the share target for X / LinkedIn. */
    @property({ type: String, attribute: 'video-url' })
    videoUrl = '';

    /** Video title — used as the email modal header. */
    @property({ type: String, attribute: 'video-title' })
    videoTitle = '';

    @state()
    private _popperOpen = false;

    private _form = new TanStackFormController(this, {
        defaultValues: { emails: '', message: '' } satisfies ShareFormValues,
        onSubmit: async ({ value, formApi }) => {
            await this._submitShare(value);
            formApi.reset();
        },
    });

    private _onEmailClick = () => {
        this._popperOpen = true;
    };

    private get _xShareUrl(): string {
        if (!this.videoUrl) return '';
        const params = new URLSearchParams({ url: this.videoUrl, via: 'vumedi' });
        if (this.videoTitle) params.set('text', this.videoTitle);
        return `https://x.com/intent/tweet?${params.toString()}`;
    }

    private _trackVideoShared(shareMethod: 'X' | 'LinkedIn' | 'email', extra: Record<string, unknown> = {}) {
        const ctx = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;
        if (!ctx?.videoId) return;
        window.vumedi?.mixpanel?.track('VideoShared', {
            share_method: shareMethod,
            video_id: ctx.videoId,
            video_slug: ctx.videoSlug,
            channel_id: ctx.channelId,
            template_version: ctx.templateVersion,
            ...extra,
        });
    }

    private _onXClick = () => {
        window.gtag?.('event', 'tweet_clicked', {
            event_category: 'Clicks',
            event_label: window.__VUMEDI_VIDEO_DETAIL_CONTEXT__?.videoId ?? '',
        });
        this._trackVideoShared('X');
    };

    private get _linkedInShareUrl(): string {
        if (!this.videoUrl) return '';
        const params = new URLSearchParams({ url: this.videoUrl });
        return `https://www.linkedin.com/sharing/share-offsite/?${params.toString()}`;
    }

    private _onLinkedInClick = () => {
        this._trackVideoShared('LinkedIn');
    };

    private _handleFormCancel = () => {
        this._popperOpen = false;
    };

    private async _submitShare({ emails, message }: ShareFormValues) {
        const { emails: parsed } = parseEmails(emails);
        try {
            await shareVideo(this.videoSlug, { to: parsed, message: message.trim() });
            this._trackVideoShared('email', { recipient_count: parsed.length });
            this._showToast({
                message: 'Video shared.',
                variant: 'confirmation',
                icon: 'check-circle-solid',
            });
            this._popperOpen = false;
        } catch (e) {
            if (!(e instanceof ApiFetchError)) {
                Sentry.captureException(e, {
                    extra: { videoSlug: this.videoSlug, action: 'share' },
                });
            }
            this._showToast({ message: 'Could not share video.', variant: 'error' });
        }
    }

    private _showToast(detail: { message: string; variant?: 'confirmation' | 'error'; icon?: string }) {
        document.dispatchEvent(new CustomEvent('vm-toast-show', { detail }));
    }

    override render() {
        return html`
            <vm-popover>
                <vm-button variant="solid" color-scheme="secondary" size="sm" slot="trigger">
                    <vm-icon-next slot="icon-leading" name="share-01" size="sm"></vm-icon-next>
                    Share
                </vm-button>
                <vm-dropdown>
                    <vm-dropdown-item @click=${this._onEmailClick}>
                        <vm-icon-next slot="icon-leading" name="mail-01" size="sm"></vm-icon-next>
                        Email video link
                    </vm-dropdown-item>
                    <a
                        class="vm-video-share-menu__anchor"
                        href=${this._xShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        @click=${this._onXClick}
                    >
                        <vm-dropdown-item>
                            <vm-icon-next slot="icon-leading" name="x-twitter" size="sm"></vm-icon-next>
                            Share on X
                        </vm-dropdown-item>
                    </a>
                    <a
                        class="vm-video-share-menu__anchor"
                        href=${this._linkedInShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        @click=${this._onLinkedInClick}
                    >
                        <vm-dropdown-item>
                            <vm-icon-next slot="icon-leading" name="linkedin" size="sm"></vm-icon-next>
                            Share on LinkedIn
                        </vm-dropdown-item>
                    </a>
                </vm-dropdown>
            </vm-popover>

            <vm-content-popper ?open=${this._popperOpen} @vm-close=${this._handleFormCancel}>
                <span slot="header" class="vm-video-share-menu__form-header">
                    ${this.videoTitle ? html`Share &ldquo;${this.videoTitle}&rdquo;` : 'Share video'}
                </span>
                <form
                    class="vm-video-share-menu__form"
                    @submit=${(e: Event) => {
                        e.preventDefault();
                        this._form.api.handleSubmit();
                    }}
                >
                    ${this._form.field(
                        {
                            name: 'emails',
                            validators: {
                                onSubmit: ({ value }) => {
                                    const { emails, invalid } = parseEmails(value);
                                    if (emails.length === 0) return 'Enter at least one e-mail address';
                                    if (invalid.length > 0) return `Invalid e-mail: ${invalid[0]}`;
                                    return undefined;
                                },
                            },
                        },
                        (field) => html`
                            <label class="vm-video-share-menu__form-label">
                                <span class="vm-video-share-menu__form-label-text">
                                    Enter one or more e-mail addresses, each in a new line
                                </span>
                                <textarea
                                    name="emails"
                                    class=${classMap({ 'has-error': field.state.meta.errors.length > 0 })}
                                    placeholder="Enter one or more e-mail addresses, each in a new line..."
                                    rows="3"
                                    .value=${field.state.value}
                                    @input=${(e: Event) => field.handleChange((e.target as HTMLTextAreaElement).value)}
                                    @blur=${() => field.handleBlur()}
                                ></textarea>
                                ${field.state.meta.errors.length
                                    ? html`<span class="vm-video-share-menu__form-error" role="alert">
                                          ${field.state.meta.errors[0]}
                                      </span>`
                                    : nothing}
                            </label>
                        `,
                    )}
                    ${this._form.field(
                        { name: 'message' },
                        (field) => html`
                            <label class="vm-video-share-menu__form-label">
                                <span class="vm-video-share-menu__form-label-text">Message</span>
                                <textarea
                                    name="message"
                                    placeholder="Write a message to your recipients..."
                                    rows="3"
                                    .value=${field.state.value}
                                    @input=${(e: Event) => field.handleChange((e.target as HTMLTextAreaElement).value)}
                                    @blur=${() => field.handleBlur()}
                                ></textarea>
                            </label>
                        `,
                    )}
                    <div class="vm-video-share-menu__form-actions">
                        <vm-button
                            variant="solid"
                            color-scheme="primary"
                            size="sm"
                            type="submit"
                            ?loading=${this._form.api.state.isSubmitting}
                        >
                            Send
                        </vm-button>
                    </div>
                </form>
            </vm-content-popper>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-share-menu': VmVideoShareMenu;
    }
}
